// ./src/ui/components/Lobby.tsx
import React, { useState } from 'react';
import { useTheme } from '@core/UI/context/ThemeContext';
import { Theme } from '@config/themes/ITheme';
import { useT } from '@core/i18n';
import { track, trackScreen } from '@core/analytics';

// ADDED: A utility function to unlock the global AudioContext.
// This should be called on the first user interaction (e.g., a click).
const unlockAudioContext = (() => {
  let unlocked = false;
  return () => {
    if (unlocked || typeof window === 'undefined') return;
    try {
      const context = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      if (context.state === 'suspended') {
        context.resume();
      }
      unlocked = true;
      console.log('Audio context unlocked by user interaction.');
    } catch (e) {
      console.error('Could not unlock audio context:', e);
    }
  };
})();

type Tile = {
  name: string;
  slug: string;
  playable: boolean;
  imageUrl: string;
};

const tiles: Tile[] = [
  {
    name: 'SkyX (Crash Game)',
    slug: 'crash',
    playable: true,
    imageUrl: '/assets/lobbyAssets/crash.jpg',
  },
  {
    name: 'Aviator - BE dummy',
    slug: 'aviator',
    playable: true,
    imageUrl: '/assets/lobbyAssets/crash.jpg',
  },
  {
    name: 'Sky Sniffer',
    slug: 'sky-sniffer',
    playable: true,
    imageUrl: '/assets/lobbyAssets/skysniffer.png',
  },
  {
    name: 'Sky Sniffer v2',
    slug: 'sky-sniffer-v2',
    playable: true,
    imageUrl: '/assets/lobbyAssets/skysniffer.png',
  },
  {
    name: 'City Sniffer',
    slug: 'city-sniffer',
    playable: true,
    imageUrl: '/assets/lobbyAssets/skysniffer.png',
  },
  {
    name: 'Super Dog',
    slug: 'superdog',
    playable: true,
    imageUrl: '/assets/lobbyAssets/skysniffer.png',
  },
  {
    name: 'Cave Crusher',
    slug: 'cave-crasher',
    playable: true,
    imageUrl: '/assets/lobbyAssets/mining.png',
  },
  {
    name: 'Chicken Luck',
    slug: 'chickenluck',
    playable: true,
    imageUrl: '/assets/lobbyAssets/chickenluck.png',
  },
  {
    name: 'NFL Kick',
    slug: 'nflkick',
    playable: true,
    imageUrl: '/assets/lobbyAssets/nflkick.jpg',
  },
  // { name: 'Pirates Fortune', slug: 'pirates-fortune', playable: true, imageUrl: '/assets/lobbyAssets/piratesfortune.png' },
  // { name: 'Pirates Awakening', slug: 'pirates-awakening', playable: true, imageUrl: '/assets/lobbyAssets/piratesawakening.jpg' },
  // { name: 'Eye of the Reels', slug: 'eye-of-the-reels', playable: true, imageUrl: '/assets/lobbyAssets/eyeofthereels.png' },
  // { name: 'Predator Path', slug: 'predator-path', playable: true, imageUrl: '/assets/lobbyAssets/predatorpath.png' }
];

const createLobbyStyles = (theme: Theme) => {
  const styles: Record<string, React.CSSProperties> = {
    wrap: {
      position: 'relative',
      padding: '48px 24px',
      color: theme.colors.text,
      background: theme.colors.background,
      height: '100vh',
      boxSizing: 'border-box',
      overflowY: 'auto',
      fontFamily: "'Inter', system-ui, sans-serif",
    },
    header: {
      maxWidth: 960,
      margin: '0 auto 40px',
      textAlign: 'center',
    },
    h1: {
      margin: 0,
      fontSize: 40,
      fontWeight: 800,
      letterSpacing: '-1px',
      textShadow: '0 2px 10px rgba(0,0,0,0.3)',
    },
    sub: {
      margin: '8px 0 0',
      fontSize: 18,
      opacity: 0.7,
      color: theme.colors.secondary,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: 24,
      maxWidth: 1200,
      margin: '0 auto',
    },
    tile: {
      position: 'relative',
      borderRadius: 16,
      overflow: 'hidden',
      border: '2px solid rgba(255,255,255,.08)',
      background: theme.colors.overlay || '#1a232b',
      boxShadow: '0 10px 30px rgba(0,0,0,.4)',
      cursor: 'pointer',
      padding: 0,
      display: 'block',
      width: '100%',
      textAlign: 'left',
      transition:
        'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
      height: '300px',
    },
    tileHover: {
      transform: 'translateY(-8px)',
      boxShadow: '0 20px 40px rgba(0,0,0,.5)',
    },
    tileImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
      transition: 'transform 0.4s ease-out',
    },
    tileImageHover: {
      transform: 'scale(1.1)',
    },
    tileOverlay: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      padding: 20,
      background:
        'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.8) 100%)',
    },
    tileTitle: {
      fontSize: 22,
      fontWeight: 700,
      color: '#fff',
      textShadow: '0 2px 6px rgba(0,0,0,.7)',
      marginBottom: '8px',
    },
    playBtn: {
      fontSize: 14,
      fontWeight: 600,
      color: '#fff',
      opacity: 0.85,
      padding: '6px 12px',
      background: 'rgba(255,255,255,.1)',
      borderRadius: 10,
      width: 'fit-content',
    },
  };

  const colorFor = (slug: string, isHovered: boolean): React.CSSProperties => {
    const hues: Record<string, number> = {
      'pirates-fortune': 28,
      'pirates-awakening': 210,
      'eye-of-the-reels': 145,
      'predator-path': 275,
      crash: 190,
      aviator: 120,
    };
    const h = hues[slug] ?? 200;

    if (isHovered) {
      return { borderColor: `hsla(${h}, 70%, 60%, 0.7)` };
    }
    return {};
  };

  return { styles, colorFor };
};

export const Lobby: React.FC = () => {
  const t = useT();
  const [hoveredSlug, setHoveredSlug] = useState<string | null>(null);
  const { theme } = useTheme();
  const { styles, colorFor } = React.useMemo(
    () => createLobbyStyles(theme),
    [theme]
  );

  React.useEffect(() => {
    trackScreen('lobby', 'Game Lobby');
  }, []);

  const onOpen = (slug: string) => {
    // CHANGED: Unlock audio on user interaction.
    unlockAudioContext();

    track('game_select', { game_slug: slug });

    const url = new URL(location.href);
    url.searchParams.set('game', slug);
    history.pushState({}, '', url.toString());
    window.dispatchEvent(new Event('playgrid:navigation'));
  };

  return (
    <div style={{ ...styles.wrap, pointerEvents: 'auto' }}>
      <header style={styles.header}>
        <h1 style={styles.h1}>play-studios.ai</h1>
        <p style={styles.sub}>{t('ui.lobby.subtitle')}</p>
      </header>

      <div style={styles.grid}>
        {tiles.map(tile => {
          const isHovered = hoveredSlug === tile.slug;
          const tileStyle = {
            ...styles.tile,
            ...(isHovered ? styles.tileHover : {}),
            ...colorFor(tile.slug, isHovered),
          };
          const imageStyle = {
            ...styles.tileImage,
            ...(isHovered ? styles.tileImageHover : {}),
          };

          return (
            <button
              key={tile.slug}
              style={tileStyle}
              onClick={() => onOpen(tile.slug)}
              onMouseEnter={() => setHoveredSlug(tile.slug)}
              onMouseLeave={() => setHoveredSlug(null)}
            >
              <img src={tile.imageUrl} alt={tile.name} style={imageStyle} />
              <div style={styles.tileOverlay}>
                <div style={styles.tileTitle}>{tile.name}</div>
                <div style={styles.playBtn}>{t('ui.lobby.play_now')}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
